import { InteractionsLogger } from './interactionsLogger';
import { InteractionsLoggerV1 } from './interactionsLoggerV1';
import {
    EventInteraction,
    EventInteractionContext,
    EventInteractions,
    PLACE_EVENT_INTERACTION,
    Subscribable,
} from '@/src/homepage/utils/tracking';

/**
 * Document-level batcher for event-card interactions. Listens for
 * `PLACE_EVENT_INTERACTION`, dedupes impressions/mounts by
 * `(id, placementId, action)`, batches the rest, and every 1.25s (and on
 * `beforeunload`) fans the batch out to every registered logger.
 *
 * Ported from `@eventbrite/consumer-search.discover-utils` (mirrors eb-ui). All
 * registered loggers — `InteractionsLoggerV1` (log_engagement) and
 * `InteractionsPicoXLogger` (PicoX spend pipeline) — therefore consume the
 * identical deduped, batched interaction stream.
 */
export class EventsInteractionsTracker {
    private LOG_PERIODICITY_MILLISECONS = 1250;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    private interactionsLoggers: InteractionsLogger<any>[];
    private interactions: EventInteraction<EventInteractionContext>[] = [];
    private interactions_history: Set<string> = new Set();
    private element: Subscribable;

    constructor(
        element: Subscribable,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        interactionsLoggers: InteractionsLogger<any>[] = [new InteractionsLoggerV1()],
    ) {
        this.element = element;
        this.interactionsLoggers = interactionsLoggers;
    }

    listen() {
        this.element.addEventListener(PLACE_EVENT_INTERACTION, ({ detail }) => {
            const interactionUniqueId = `${detail.id}-${detail.place?.placementId}-${detail.action}`;

            if (
                uniqueInteractions.includes(detail.action) &&
                this.interactions_history.has(interactionUniqueId)
            ) {
                return;
            }

            this.interactions_history.add(interactionUniqueId);
            this.interactions.push(detail);
        });
    }

    track() {
        setInterval(() => {
            this.logStoredInteractions();
        }, this.LOG_PERIODICITY_MILLISECONS);

        // `beforeunload` only fires on `window`, never on `document` (the tracker's
        // `element`) or arbitrary elements, so it must be registered here directly.
        window.addEventListener('beforeunload', () => this.logStoredInteractions());
    }

    private logStoredInteractions() {
        const logsLimit = 10;
        for (let i = 0; i < this.interactions.length; i += logsLimit) {
            const logsBlock = this.interactions.slice(i, i + logsLimit);
            this.interactionsLoggers.forEach((il) => {
                il.run(logsBlock);
            });
        }
        this.interactions = [];
    }
}

// This interactions should be only logged once
const uniqueInteractions = [EventInteractions.Impression, EventInteractions.Mounted];
